/**
 * Main hook for campaign duration management
 * Replaces the old useDateSelection hook with cleaner architecture
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { DateStorageService } from '../services/DateStorageService';
import { DateValidationService } from '../services/DateValidationService';
import { DurationCalculationService } from '../services/DurationCalculationService';
import { useCalendarDisplay } from './useCalendarDisplay';
import { useAdminBypass } from '@/contexts/AdminBypassContext';
import type { CampaignDurationHook } from '../types/DurationTypes';
import { devLog, devWarn } from '@/utils/secureLogger';

export const useCampaignDuration = (
  onChange: (value: number[]) => void,
  isActive: boolean = true
): CampaignDurationHook => {
  // Check if admin can bypass date restrictions
  const { canBypassDateRestrictions } = useAdminBypass();
  
  // Initialize minimum date (admin bypass allows selecting from now)
  const minDate = DateValidationService.getMinimumDate(canBypassDateRestrictions);
  
  // Use ref for stable onChange callback
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  
  // Lazy state initialization - only runs once on mount
  const [initialData] = useState(() => {
    // Skip expensive initialization if not active
    if (!isActive) {
      return {
        startDate: null,
        endDate: null,
        startDisplayDate: new Date(),
        endDisplayDate: new Date()
      };
    }
    
    const stored = DateStorageService.loadSavedDates();
    devLog('📅 Loading stored campaign dates:', stored);
    
    const startDate = DateStorageService.parseStoredDate(stored.startDate, minDate);
    const endDate = DateStorageService.parseStoredDate(stored.endDate, minDate);
    
    // Validate loaded dates
    const { validStartDate, validEndDate } = DateValidationService.validateLoadedDates(
      startDate, 
      endDate
    );
    
    // Set display dates to show the month of selected dates, or current month
    const startDisplayDate = validStartDate 
      ? new Date(validStartDate.getFullYear(), validStartDate.getMonth(), 1)
      : new Date();
    
    const endDisplayDate = validEndDate 
      ? new Date(validEndDate.getFullYear(), validEndDate.getMonth(), 1)
      : new Date();
    
    devLog('📅 Initial dates loaded:', {
      startDate: validStartDate?.toISOString(),
      endDate: validEndDate?.toISOString(),
      startDisplayMonth: startDisplayDate.toISOString().slice(0, 7),
      endDisplayMonth: endDisplayDate.toISOString().slice(0, 7)
    });
    
    return {
      startDate: validStartDate,
      endDate: validEndDate,
      startDisplayDate,
      endDisplayDate
    };
  });
  
  // State management - initialized from lazy computation
  const [startDate, setStartDate] = useState<Date | null>(initialData.startDate);
  const [endDate, setEndDate] = useState<Date | null>(initialData.endDate);
  const [duration, setDuration] = useState<number>(0);
  
  // Calendar display management
  const {
    startDisplayDate,
    endDisplayDate,
    setStartDisplayDate,
    setEndDisplayDate,
    navigateCalendar,
    updateDisplayDatesFromSelection,
    syncEndDisplayToStart
  } = useCalendarDisplay(initialData.startDisplayDate, initialData.endDisplayDate);
  
  // Listen for external changes to localStorage and custom date change events - only when active
  useEffect(() => {
    if (!isActive) return; // Skip if not active mode
    
    const handleStorageChange = () => {
      devLog('🔄 Storage changed, reloading dates');
      const stored = DateStorageService.loadSavedDates();

      const rawStart = stored.startDate;
      const rawEnd = stored.endDate;

      const parsedStart = DateStorageService.parseStoredDate(rawStart, minDate);
      const parsedEnd = DateStorageService.parseStoredDate(rawEnd, minDate);

      // Only apply non-empty, valid values from storage to avoid wiping user selections
      if (rawStart && parsedStart && parsedStart.getTime() !== startDate?.getTime()) {
        devLog('📅 Updated startDate from storage:', parsedStart.toISOString());
        setStartDate(parsedStart);
      }

      if (rawEnd && parsedEnd && parsedEnd.getTime() !== endDate?.getTime()) {
        devLog('📅 Updated endDate from storage:', parsedEnd.toISOString());
        setEndDate(parsedEnd);
      }
    };

    // Listen for both native storage events (cross-tab) and custom events (same-tab)
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('campaignDatesChanged', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('campaignDatesChanged', handleStorageChange);
    };
  }, [startDate?.getTime(), endDate?.getTime(), minDate, isActive]);
  
  // Debounced duration calculation when dates change - only when active
  useEffect(() => {
    if (!isActive) return; // Skip if not active mode
    
    // Debounce to prevent excessive updates
    const timeoutId = setTimeout(() => {
      if (startDate && endDate) {
        const calculatedDuration = DurationCalculationService.calculateDuration(startDate, endDate);
        if (calculatedDuration !== duration) {
          setDuration(calculatedDuration);
          // Only update onChange when duration actually changes
          onChangeRef.current([calculatedDuration]);
        }
      } else {
        if (duration !== 0) {
          setDuration(0);
          // Only update onChange when duration actually changes to 0
          onChangeRef.current([0]);
        }
      }
    }, 50); // 50ms debounce
    
    return () => clearTimeout(timeoutId);
  }, [startDate?.getTime(), endDate?.getTime(), duration, isActive]);
  
  // Start date selection handler
  const selectStartDate = (day: number, displayDate: Date) => {
    const selectedDate = DateValidationService.createStartOfDay(
      displayDate.getFullYear(),
      displayDate.getMonth(),
      day
    );
    
    // Validate the selection
    const validation = DateValidationService.validateStartDate(selectedDate, endDate, minDate);
    if (!validation.isValid) {
      devWarn('Start date validation failed:', validation.error);
      return;
    }
    
    // Update state and storage
    setStartDate(selectedDate);
    DateStorageService.saveDate(selectedDate, 'start');
    
    // Handle end date clearing if needed
    if (validation.shouldClearEndDate) {
      setEndDate(null);
      DateStorageService.clearEndDate();
      devLog('Cleared end date due to start date change');
    }
    
    // Update display dates
    updateDisplayDatesFromSelection(selectedDate, true);
    
    // Sync end calendar to help with consecutive day selection
    if (!endDate) {
      syncEndDisplayToStart(selectedDate);
    }
    
    // Calculate duration if both dates exist
    if (endDate && !validation.shouldClearEndDate) {
      DurationCalculationService.processDateSelectionComplete(selectedDate, endDate, onChangeRef.current);
    }
  };
  
  // End date selection handler
  const selectEndDate = (day: number, displayDate: Date) => {
    const selectedDate = DateValidationService.createEndOfDay(
      displayDate.getFullYear(),
      displayDate.getMonth(),
      day
    );
    
    // Validate the selection
    const validation = DateValidationService.validateEndDate(selectedDate, startDate);
    if (!validation.isValid) {
      devWarn('End date validation failed:', validation.error);
      return;
    }
    
    // Update state and storage
    setEndDate(selectedDate);
    DateStorageService.saveDate(selectedDate, 'end');
    
    // Update display dates
    updateDisplayDatesFromSelection(selectedDate, false);
    
    // Calculate and save duration
    if (startDate) {
      DurationCalculationService.processDateSelectionComplete(startDate, selectedDate, onChangeRef.current);
    }
  };
  
  return {
    // State
    startDate,
    endDate,
    startDisplayDate,
    endDisplayDate,
    minDate,
    duration,
    
    // Actions
    selectStartDate,
    selectEndDate,
    navigateCalendar,
    setDisplayDates: {
      setStartDisplayDate,
      setEndDisplayDate
    }
  };
};
